import { useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { useNavigate } from 'react-router'
import toast from 'react-hot-toast'
import { useAuth } from '../../hooks/useAuth'
import { consumeAuthReturnTo, normalizeReturnTo } from '../../lib/authReturnTo'
import { supabase, signInWithApple, signInWithGoogle } from '../../lib/supabase'
import {
  trackDataFastSignupPendingConfirmation,
  trackDataFastSignupStarted,
} from '../../lib/analytics'
import { isDisposableEmail } from '../../lib/disposable-emails'
import { SIGNUP_COMPLETED_KEY } from '../../pages/SignUpSuccessPage'
import { storePasswordCredential } from '../../lib/passwordCredentials'
import {
  flushPendingSignupAttribution,
  markPendingSignupAccount,
  clearPendingSignupAttribution,
  isNewSignupAccount,
  prepareSignupAttribution,
  trackFunnelEvent,
} from '../../lib/signupAttribution'
import { isNativeIOS } from '../../mobile/capacitor'
import { withMarketingQuery } from '../../utils/marketingLinks'

type FormState = {
  fullName: string
  email: string
  password: string
  confirmPassword: string
}

type Errors = Partial<Record<keyof FormState, string>> & { form?: string; success?: string }

const initialState: FormState = { fullName: '', email: '', password: '', confirmPassword: '' }

export default function SignUpForm() {
  const { signUp } = useAuth()
  const navigate = useNavigate()
  const [values, setValues] = useState<FormState>(initialState)
  const [errors, setErrors] = useState<Errors>({})
  const [submitting, setSubmitting] = useState(false)
  const [showEmailVerification, setShowEmailVerification] = useState(false)
  const [signupEmail, setSignupEmail] = useState('')
  const [resendingEmail, setResendingEmail] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [appleLoading, setAppleLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const validate = (v: FormState): Errors => {
    const newErrors: Errors = {}
    if (!v.fullName.trim() || v.fullName.trim().length < 2) {
      newErrors.fullName = 'Please enter your full name (at least 2 characters)'
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email)) {
      newErrors.email = 'Enter a valid email'
    } else if (isDisposableEmail(v.email)) {
      newErrors.email =
        'Temporary/disposable email addresses are not allowed. Please use a permanent email.'
    }
    if (v.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters'
    } else if (
      !/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`])/.test(v.password)
    ) {
      newErrors.password = 'Password must include uppercase, lowercase, number, and symbol'
    }
    if (v.confirmPassword !== v.password) newErrors.confirmPassword = 'Passwords do not match'
    return newErrors
  }

  const handleGoogleSignIn = async () => {
    try {
      setGoogleLoading(true)
      setErrors({})
      // Set intent flag to distinguish from login in AuthCallbackPage
      sessionStorage.setItem('optioneer_signup_intent', 'true')
      await prepareSignupAttribution()
      void trackFunnelEvent('signup_started')
      trackDataFastSignupStarted('google')
      await signInWithGoogle()
      setGoogleLoading(false)
    } catch (err) {
      sessionStorage.removeItem('optioneer_signup_intent')
      setGoogleLoading(false)
      const errorMsg = err instanceof Error ? err.message : 'Failed to sign in with Google'
      setErrors({ form: errorMsg })
      toast.error(errorMsg)
    }
  }

  const handleAppleSignIn = async () => {
    setAppleLoading(true)
    setErrors({})
    sessionStorage.setItem('optioneer_signup_intent', 'true')

    try {
      await prepareSignupAttribution()
      void trackFunnelEvent('signup_started')
      trackDataFastSignupStarted('apple')
      const result = await signInWithApple()
      sessionStorage.removeItem('optioneer_signup_intent')
      if (result.user && isNewSignupAccount(result.user)) {
        markPendingSignupAccount(result.user.id, result.user.created_at)
        await flushPendingSignupAttribution()
      } else {
        clearPendingSignupAttribution()
      }
      sessionStorage.setItem(SIGNUP_COMPLETED_KEY, 'true')

      const queryReturnTo = normalizeReturnTo(
        new URLSearchParams(window.location.search).get('returnTo') ||
          new URLSearchParams(window.location.search).get('redirect')
      )
      const storedReturnTo = consumeAuthReturnTo()
      navigate(withMarketingQuery('/signup/success', window.location.search), {
        state: { redirectTo: queryReturnTo || storedReturnTo || '/trial-activation' },
      })
    } catch (err) {
      sessionStorage.removeItem('optioneer_signup_intent')
      const errorMsg = err instanceof Error ? err.message : 'Failed to sign in with Apple'
      setErrors({ form: errorMsg })
      toast.error(errorMsg)
    } finally {
      setAppleLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    const next = { ...values, [name]: value }
    setValues(next)
  }

  const handleResendEmail = async () => {
    setResendingEmail(true)
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: signupEmail,
    })
    setResendingEmail(false)

    if (error) {
      const errorMessage = `Failed to resend email: ${error.message}`
      setErrors({ form: errorMessage })
      toast.error(errorMessage)
    } else {
      const successMessage = 'Confirmation email resent! Check your inbox and spam folder.'
      setErrors({ success: successMessage })
      toast.success(successMessage)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    setShowEmailVerification(false)
    const vErrors = validate(values)
    if (Object.keys(vErrors).length > 0) {
      setErrors(vErrors)
      const firstError = Object.values(vErrors)[0]
      if (firstError) toast.error(firstError)
      return
    }
    setSubmitting(true)
    await prepareSignupAttribution()
    void trackFunnelEvent('signup_started')
    trackDataFastSignupStarted('email')
    const res = await signUp({
      email: values.email,
      password: values.password,
      fullName: values.fullName.trim(),
    })
    setSubmitting(false)
    if (res.error) {
      clearPendingSignupAttribution()
      const isEmailServiceError =
        res.error.includes('500') ||
        res.error.toLowerCase().includes('email') ||
        res.error.toLowerCase().includes('smtp')

      const errorMessage = isEmailServiceError
        ? 'Email service is not configured. Please try signing up with Google instead, or contact support.'
        : res.error

      setErrors({ form: errorMessage })
      toast.error(errorMessage)
      return
    }

    markPendingSignupAccount(res.userId, res.createdAt)

    if (res.needsConfirmation) {
      await storePasswordCredential({
        email: values.email,
        password: values.password,
        name: values.fullName,
      })
      trackDataFastSignupPendingConfirmation('email')
      setSignupEmail(values.email)
      setShowEmailVerification(true)
      toast.success('Account created! Please check your email to verify your account.')
    } else {
      await storePasswordCredential({
        email: values.email,
        password: values.password,
        name: values.fullName,
      })
      void flushPendingSignupAttribution()
      // Successful signup without email confirmation
      // Set flag for signup success page
      sessionStorage.setItem(SIGNUP_COMPLETED_KEY, 'true')
      const successMessage = 'Account created successfully! You are now logged in.'
      setErrors({ success: successMessage })
      toast.success(successMessage)

      let redirectTo = '/trial-activation'

      const queryReturnTo = normalizeReturnTo(
        new URLSearchParams(window.location.search).get('returnTo') ||
          new URLSearchParams(window.location.search).get('redirect')
      )
      const storedReturnTo = consumeAuthReturnTo()
      if (queryReturnTo || storedReturnTo) {
        redirectTo = queryReturnTo || storedReturnTo || redirectTo
      }

      // Redirect to signup success page with redirect state
      navigate(withMarketingQuery('/signup/success', window.location.search), {
        state: { redirectTo },
      })
    }

    setValues(initialState)
    // Don't call onSuccess here - we're handling navigation ourselves
  }

  if (showEmailVerification) {
    return (
      <div className="w-full space-y-6">
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-emerald-900/20 dark:to-green-900/20 border-2 border-green-300 dark:border-emerald-700 rounded-xl p-4 shadow-lg space-y-4 sm:p-6">
          <div className="flex flex-col items-center text-center space-y-3">
            <div className="bg-green-500 dark:bg-emerald-500 rounded-full p-3 sm:p-4">
              <svg
                className="h-10 w-10 text-white sm:h-12 sm:w-12"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div className="w-full min-w-0">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white sm:text-2xl">
                Check Your Email!
              </h3>
              <p className="mt-2 text-sm text-slate-700 dark:text-slate-300 sm:text-base">
                We've sent a confirmation link to:
              </p>
              <p className="mt-1 max-w-full break-words text-base font-semibold leading-snug text-green-700 [overflow-wrap:anywhere] dark:text-emerald-400 sm:text-lg">
                {signupEmail}
              </p>
            </div>
            <div className="bg-white dark:bg-slate-800 border border-green-200 dark:border-emerald-700 rounded-lg p-4 w-full text-left space-y-2">
              <p className="text-sm text-slate-800 dark:text-slate-200 font-medium">
                What to do next:
              </p>
              <ol className="list-decimal space-y-1 pl-5 text-sm text-slate-700 dark:text-slate-300">
                <li>Check your inbox (and spam/junk folder)</li>
                <li>Click the confirmation link in the email</li>
                <li>You'll be redirected back and logged in automatically</li>
              </ol>
            </div>
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4 w-full text-left space-y-2">
              <p className="text-sm text-amber-800 dark:text-amber-300 font-medium">
                Didn't receive the email?
              </p>
              <ul className="list-disc space-y-1 pl-5 text-sm text-amber-700 dark:text-amber-400">
                <li>
                  Check your <strong>spam/junk folder</strong>
                </li>
                <li>Make sure you entered the correct email address</li>
                <li>Wait a few minutes - emails can sometimes be delayed</li>
                <li>Try the "Resend" button below</li>
              </ul>
            </div>
          </div>
        </div>

        {errors.form && (
          <div
            role="alert"
            className="text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3"
          >
            {errors.form}
          </div>
        )}
        {errors.success && (
          <div
            role="status"
            className="text-green-600 dark:text-emerald-400 text-sm bg-green-50 dark:bg-emerald-900/20 border border-green-200 dark:border-emerald-800 rounded p-3"
          >
            {errors.success}
          </div>
        )}

        <button
          type="button"
          onClick={handleResendEmail}
          disabled={resendingEmail}
          className="w-full rounded-md bg-brand-600 dark:bg-amber-500 text-white dark:text-slate-900 py-2 disabled:opacity-60 hover:bg-brand-700 dark:hover:bg-amber-400 transition-colors font-semibold"
        >
          {resendingEmail ? 'Sending...' : 'Resend Confirmation Email'}
        </button>

        <div className="text-center text-sm text-slate-600 dark:text-slate-400">
          Already confirmed your email?{' '}
          <a
            href="/signin"
            className="text-brand-600 dark:text-amber-400 hover:text-brand-700 dark:hover:text-amber-300 font-medium"
          >
            Sign in here
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full space-y-4">
      {errors.form && (
        <div
          role="alert"
          className="text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3"
        >
          {errors.form}
        </div>
      )}
      {errors.success && (
        <div
          role="status"
          className="text-green-600 dark:text-emerald-400 text-sm bg-green-50 dark:bg-emerald-900/20 border border-green-200 dark:border-emerald-800 rounded p-3"
        >
          {errors.success}
        </div>
      )}

      {isNativeIOS() && (
        <button
          type="button"
          onClick={handleAppleSignIn}
          disabled={appleLoading || googleLoading || submitting}
          aria-busy={appleLoading}
          className="flex w-full items-center justify-center gap-3 rounded-md border border-black bg-black px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-900 disabled:opacity-60"
        >
          <span className="text-xl leading-none" aria-hidden="true">
            &#63743;
          </span>
          <span>{appleLoading ? 'Connecting to Apple...' : 'Continue with Apple'}</span>
        </button>
      )}

      <button
        type="button"
        onClick={handleGoogleSignIn}
        disabled={googleLoading || appleLoading || submitting}
        className="w-full flex items-center justify-center gap-3 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-60 transition-colors"
      >
        <svg className="h-5 w-5" viewBox="0 0 24 24">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        <span>{googleLoading ? 'Connecting to Google...' : 'Continue with Google'}</span>
      </button>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-slate-300 dark:border-slate-700" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="bg-white dark:bg-slate-900 px-2 text-slate-500 dark:text-slate-400">
            or
          </span>
        </div>
      </div>

      <form id="signup-form" onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
        <div className="space-y-1">
          <label
            htmlFor="signup-name"
            className="block text-sm font-medium text-slate-700 dark:text-slate-300"
          >
            Full Name
          </label>
          <input
            id="signup-name"
            name="fullName"
            type="text"
            autoComplete="name"
            value={values.fullName}
            onChange={handleChange}
            className="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:border-brand-500 dark:focus:border-amber-500 focus:ring-1 focus:ring-brand-500 dark:focus:ring-amber-500 focus:outline-none transition-colors"
            placeholder="John Doe"
          />
          {errors.fullName && (
            <p role="alert" className="text-red-600 dark:text-red-400 text-sm">
              {errors.fullName}
            </p>
          )}
        </div>

        <div className="space-y-1">
          <label
            htmlFor="signup-username"
            className="block text-sm font-medium text-slate-700 dark:text-slate-300"
          >
            Email
          </label>
          <input
            id="signup-username"
            name="email"
            type="email"
            autoComplete="username"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={values.email}
            onChange={handleChange}
            className="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:border-brand-500 dark:focus:border-amber-500 focus:ring-1 focus:ring-brand-500 dark:focus:ring-amber-500 focus:outline-none transition-colors"
            placeholder="you@example.com"
          />
          {errors.email && (
            <p className="text-red-600 dark:text-red-400 text-xs" role="alert">
              {errors.email}
            </p>
          )}
        </div>

        <div className="space-y-1">
          <label
            htmlFor="signup-new-password"
            className="block text-sm font-medium text-slate-700 dark:text-slate-300"
          >
            Password
          </label>
          <div className="relative">
            <input
              id="signup-new-password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={values.password}
              onChange={handleChange}
              className="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 pr-11 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:border-brand-500 dark:focus:border-amber-500 focus:ring-1 focus:ring-brand-500 dark:focus:ring-amber-500 focus:outline-none transition-colors"
              placeholder="********"
            />
            <button
              type="button"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
              onClick={() => setShowPassword((current) => !current)}
              className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-md text-slate-500 transition-colors hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-slate-400 dark:hover:text-slate-200 dark:focus-visible:ring-amber-500"
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" aria-hidden="true" />
              ) : (
                <Eye className="h-5 w-5" aria-hidden="true" />
              )}
            </button>
          </div>
          {errors.password && (
            <p className="text-red-600 dark:text-red-400 text-xs" role="alert">
              {errors.password}
            </p>
          )}
        </div>

        <div className="space-y-1">
          <label
            htmlFor="signup-confirm-password"
            className="block text-sm font-medium text-slate-700 dark:text-slate-300"
          >
            Confirm Password
          </label>
          <div className="relative">
            <input
              id="signup-confirm-password"
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="new-password"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={values.confirmPassword}
              onChange={handleChange}
              className="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 pr-11 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:border-brand-500 dark:focus:border-amber-500 focus:ring-1 focus:ring-brand-500 dark:focus:ring-amber-500 focus:outline-none transition-colors"
              placeholder="********"
            />
            <button
              type="button"
              aria-label={
                showConfirmPassword ? 'Hide password confirmation' : 'Show password confirmation'
              }
              aria-pressed={showConfirmPassword}
              onClick={() => setShowConfirmPassword((current) => !current)}
              className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-md text-slate-500 transition-colors hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-slate-400 dark:hover:text-slate-200 dark:focus-visible:ring-amber-500"
            >
              {showConfirmPassword ? (
                <EyeOff className="h-5 w-5" aria-hidden="true" />
              ) : (
                <Eye className="h-5 w-5" aria-hidden="true" />
              )}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="text-red-600 dark:text-red-400 text-xs" role="alert">
              {errors.confirmPassword}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={submitting || googleLoading || appleLoading}
          className="w-full rounded-md bg-brand-600 dark:bg-amber-500 text-white dark:text-slate-900 py-2 disabled:opacity-60 hover:bg-brand-700 dark:hover:bg-amber-400 transition-colors font-semibold"
        >
          {submitting ? 'Signing up...' : 'Create account'}
        </button>
      </form>
    </div>
  )
}
